import { useCallback, useEffect, useRef, useState } from 'react';
import { BrandLockup, BrandMark, Candles, Crown, Icon, ZodiacWheel } from './components/Decor';
import { ARTICLE_TOPICS, COURSE_LEVELS, INTERESTS, JOURNEY, NAV, OWNER_ALT, OWNER_PORTRAIT, OWNER_PORTRAIT_SIZE, OWNER_PORTRAIT_SM, OWNER_PORTRAIT_SM_SIZE, PILLARS } from './content';
import { api } from './api';
import { PolicyBody } from './shared/Policy';
import { COURSE_STATUS_LABEL, ENROLL_LABEL, type Enrollment, LEVEL_LABEL, LEVEL_NUM, VERDICT_LABEL, thaiDate, thb, toISODate, type Article, type Clip, type Course, type HoroEntry, type NewsItem, type Privacy, type PublicMember } from './shared/types';
import { Link, navigate } from './site/router';
import { ArticleCard, ClipCard, NewsCard } from './site/cards';
import { useModalDialog } from './site/dialog';
import artCosmos from './assets/art/art-cosmos.svg';
import artTrade from './assets/art/art-trade.svg';
import artHoro from './assets/art/art-horo.svg';
import artLive from './assets/art/art-live.svg';
import moon from './assets/moon.webp';
import saturn from './assets/saturn.webp';
import earth from './assets/earth.webp';

export type Notify = (feature: string) => void;

export function useToast() {
  const [msg, setMsg] = useState<string | null>(null);
  const timer = useRef<number | undefined>(undefined);
  const say = useCallback((text: string) => {
    window.clearTimeout(timer.current);
    setMsg(text);
    timer.current = window.setTimeout(() => setMsg(null), 3600);
  }, []);
  const notify: Notify = useCallback((feature: string) => say(`“${feature}” ยังไม่เปิดใช้งาน — รอเชื่อมต่อระบบหลังบ้าน`), [say]);
  return { msg, notify, say, close: () => setMsg(null) };
}

export function scrollToId(id: string, focusId?: string) {
  const el = document.getElementById(id);
  if (!el) return;
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  el.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
  const target = (focusId && document.getElementById(focusId)) || el;
  window.setTimeout(() => (target as HTMLElement).focus({ preventScroll: true }), reduce ? 0 : 450);
}

/** Interest to pre-tick when the sign-up form mounts on another page. */
let pendingInterest: string | null = null;
/** Course chosen on a course page before the visitor had an account. */
let pendingCourse = '';

/** CRM entry point: every "สมัครสมาชิก" goes to the sign-up form, optionally pre-ticking an interest / course. */
export function goJoin(interest?: string, courseId?: string) {
  if (courseId) pendingCourse = courseId;
  if (document.getElementById('join')) {
    if (interest) {
      const box = document.getElementById(`int-${interest}`) as HTMLInputElement | null;
      if (box) box.checked = true;
    }
    const sel = document.getElementById('join-course') as HTMLSelectElement | null;
    if (sel && courseId) sel.value = courseId;
    scrollToId('join', 'join-name');
    return;
  }
  pendingInterest = interest ?? null;
  navigate('/member', { focus: 'join-name' });
}

/** Member session as the website sees it. `online` = the API answered (false for dist-single / static hosting). */
export interface Auth {
  member: PublicMember | null;
  /** The member's courses from the CRM (interest → enrolled → completed). */
  enrollments: Enrollment[];
  setEnrollments: (e: Enrollment[]) => void;
  online: boolean;
  openLogin: () => void;
  logout: () => void;
  signedIn: (m: PublicMember) => void;
  toast: (text: string) => void;
}
const firstName = (n: string) => n.trim().split(/\s+/)[0];


export function LoginDialog({ onClose, onDone }: { onClose: () => void; onDone: (m: PublicMember) => void }) {
  const ref = useModalDialog(onClose);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async (e: { preventDefault(): void; currentTarget: HTMLFormElement }) => {
    e.preventDefault();
    const d = new FormData(e.currentTarget);
    const identifier = String(d.get('identifier') ?? '').trim();
    const password = String(d.get('password') ?? '');
    if (!identifier || !password) return setErr('กรุณากรอกอีเมลหรือเบอร์โทร และรหัสผ่าน');
    setBusy(true);
    setErr('');
    const r = await api<{ member: PublicMember }>('POST', '/api/member/login', { identifier, password });
    setBusy(false);
    if (r.ok) onDone(r.data.member);
    else setErr(r.offline ? 'เชื่อมต่อระบบไม่ได้ กรุณาลองใหม่' : r.error);
  };
  return (
    <dialog ref={ref} className="policy-dialog auth-dialog" aria-labelledby="login-title">
      <div className="policy-head">
        <h2 id="login-title" className="h2">เข้าสู่ระบบสมาชิก</h2>
        <button type="button" className="btn btn-icon" aria-label="ปิด" onClick={onClose}><Icon name="close" /></button>
      </div>
      <form className="auth-form" onSubmit={submit} noValidate>
        <label className="field">
          <span className="field-label">อีเมล หรือเบอร์โทร</span>
          <input name="identifier" className="input" autoComplete="username" autoFocus />
        </label>
        <label className="field">
          <span className="field-label">รหัสผ่าน</span>
          <input name="password" type="password" className="input" autoComplete="current-password" />
        </label>
        {err && <p className="form-errors" role="alert">{err}</p>}
        <button type="submit" className="btn btn-primary btn-lg" disabled={busy}>{busy ? 'กำลังเข้าสู่ระบบ…' : 'เข้าสู่ระบบ'}</button>
        <p className="fine">ลืมรหัสผ่าน? ติดต่อทีมงานโหราเทรดเดอร์เพื่อขอรหัสผ่านชั่วคราว</p>
        <p className="auth-switch">
          ยังไม่มีบัญชี?{' '}
          <button type="button" className="link-btn" onClick={() => { onClose(); goJoin(); }}>สมัครสมาชิก</button>
        </p>
      </form>
    </dialog>
  );
}

const isCurrent = (to: string, path: string) => (to === '/' ? path === '/' : path === to || path.startsWith(`${to}/`));

export function Header({ notify, auth, path }: { notify: Notify; auth: Auth; path: string }) {
  const [open, setOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const btnRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const first = panelRef.current?.querySelector<HTMLElement>('a,button');
    first?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        btnRef.current?.focus();
      }
    };
    const mq = window.matchMedia('(min-width: 1200px)');
    const onMq = () => mq.matches && setOpen(false);
    document.addEventListener('keydown', onKey);
    mq.addEventListener('change', onMq);
    document.body.classList.add('nav-open');
    return () => {
      document.removeEventListener('keydown', onKey);
      mq.removeEventListener('change', onMq);
      document.body.classList.remove('nav-open');
    };
  }, [open]);

  // Close the mobile menu after a page change.
  useEffect(() => { setOpen(false); }, [path]);

  const links = NAV.map((n) => (
    <li key={n.to}>
      <Link to={n.to} current={isCurrent(n.to, path)}>{n.label}</Link>
    </li>
  ));

  return (
    <header className="site-header">
      <div className="container header-row">
        <Link to="/" className="brand" label="โหราเทรดเดอร์ ASTRO TRADER — หน้าหลัก">
          <BrandLockup size={48} />
        </Link>
        <nav className="nav-desktop" aria-label="เมนูหลัก">
          <ul>{links}</ul>
        </nav>
        <div className="header-actions">
          {auth.member ? (
            <>
              <button type="button" className="btn btn-ghost member-chip hide-sm" onClick={() => goJoin()}>
                <Icon name="user" /> {firstName(auth.member.name)}
              </button>
              <button type="button" className="btn btn-outline hide-sm" onClick={auth.logout}>ออกจากระบบ</button>
            </>
          ) : (
            <>
              <button type="button" className="btn btn-ghost hide-sm" onClick={() => (auth.online ? auth.openLogin() : notify('เข้าสู่ระบบ'))}>เข้าสู่ระบบ</button>
              <button type="button" className="btn btn-primary hide-sm" onClick={() => goJoin()}>สมัครสมาชิก</button>
            </>
          )}
          <button
            ref={btnRef}
            type="button"
            className="btn btn-icon menu-toggle"
            aria-expanded={open}
            aria-controls="mobile-nav"
            aria-label={open ? 'ปิดเมนู' : 'เปิดเมนู'}
            onClick={() => setOpen((v) => !v)}
          >
            <Icon name={open ? 'close' : 'menu'} />
          </button>
        </div>
      </div>
      <div id="mobile-nav" ref={panelRef} className={`nav-mobile ${open ? 'is-open' : ''}`} hidden={!open}>
        <nav aria-label="เมนูมือถือ">
          <ul>{links}</ul>
        </nav>
        <div className="nav-mobile-actions">
          {auth.member ? (
            <>
              <button type="button" className="btn btn-ghost" onClick={() => { setOpen(false); goJoin(); }}><Icon name="user" /> {firstName(auth.member.name)}</button>
              <button type="button" className="btn btn-outline" onClick={() => { setOpen(false); auth.logout(); }}>ออกจากระบบ</button>
            </>
          ) : (
            <>
              <button type="button" className="btn btn-ghost" onClick={() => { setOpen(false); if (auth.online) auth.openLogin(); else notify('เข้าสู่ระบบ'); }}>เข้าสู่ระบบ</button>
              <button type="button" className="btn btn-primary" onClick={() => { setOpen(false); goJoin(); }}>สมัครสมาชิก</button>
            </>
          )}
        </div>
      </div>
      {open && <button type="button" className="nav-scrim" aria-label="ปิดเมนู" tabIndex={-1} onClick={() => setOpen(false)} />}
    </header>
  );
}

export function Ornament() {
  return (
    <span className="ornament" aria-hidden="true">
      <span className="orn-line" />
      <svg viewBox="0 0 24 24" width="14" height="14"><path d="M12 0 14 10 24 12 14 14 12 24 10 14 0 12 10 10Z" fill="currentColor" /></svg>
      <span className="orn-line" />
    </span>
  );
}

export function SectionHead({ id, eyebrow, title, sub, action }: { id: string; eyebrow: string; title: string; sub?: string; action?: JSX.Element }) {
  return (
    <div className="sec-head">
      <p className="sec-eyebrow">{eyebrow}</p>
      <h2 id={id} className="sec-title">{title}</h2>
      <Ornament />
      {sub && <p className="sec-sub">{sub}</p>}
      {action}
    </div>
  );
}

export function Hero() {
  return (
    <section className="hero" id="top" tabIndex={-1} aria-labelledby="hero-title">
      <div className="container hero-grid">
        <div className="hero-copy">
          <Candles className="hero-candles" count={20} />
          <p className="eyebrow">ASTRO TRADER</p>
          <h1 id="hero-title" className="hero-title">
            <span className="gold-text">โหราเทรดเดอร์</span>
          </h1>
          <p className="hero-lead"><span className="nobr">เรียนเทรดอย่างมีวินัย</span> <span className="nobr">พร้อมเช็กดวงการเทรด</span></p>
          <p className="hero-sub">คอร์สสอนเทรดจากพื้นฐานสู่ขั้นสูง ฝึกฝนกับข้อมูลจำลอง และเช็กดาวเข้า ดาวย้ายออก ตามวันที่ได้ในพื้นที่สมาชิก</p>
          <div className="hero-cta">
            <button type="button" className="btn btn-primary btn-lg" onClick={() => goJoin()}>
              สมัครสมาชิก <Icon name="arrow" />
            </button>
            <Link to="/courses" className="btn btn-outline btn-lg">ดูคอร์สเทรด</Link>
          </div>
        </div>

        <div className="hero-visual">
          <img className="hero-cosmos" src={artCosmos} alt="" width={640} height={400} />
          <div className="hero-vignette" />
          <ZodiacWheel className="hero-wheel" />
          {OWNER_PORTRAIT ? (
            <>
              <div className="owner-glow" aria-hidden="true" />
              <img className="owner-cutout" src={OWNER_PORTRAIT} alt={OWNER_ALT} width={OWNER_PORTRAIT_SIZE.w} height={OWNER_PORTRAIT_SIZE.h} />
              <div className="hero-ground" aria-hidden="true" />
            </>
          ) : (
            <figure className="owner-slot">
              <span className="owner-icon"><Icon name="user" /></span>
              <figcaption>
                <strong>พื้นที่ภาพเจ้าของ</strong>
                <span>รอไฟล์ภาพต้นฉบับที่ได้รับอนุญาต</span>
              </figcaption>
            </figure>
          )}
        </div>
      </div>
    </section>
  );
}


export function Pillars({ notify }: { notify: Notify }) {
  return (
    <section className="section" aria-labelledby="pillars-title">
      <div className="container">
        <SectionHead id="pillars-title" eyebrow="TRADER × ASTRO" title="เทรดเดอร์ × โหรา" sub="เรียนและฝึกเทรดอย่างมีวินัย พร้อมเครื่องมือเช็กดวงการเทรดสำหรับสมาชิก" />
        <div className="pillars">
          {PILLARS.map((p) => (
            <article key={p.id} id={p.id} tabIndex={-1} className={`pillar pillar-${p.id}`} aria-labelledby={`${p.id}-t`}>
              <div className="pillar-art" aria-hidden="true">
                <img src={p.img} alt="" width={176} height={153} />
              </div>
              <div className="pillar-body">
                <p className="pillar-eyebrow">{p.eyebrow}</p>
                <h3 id={`${p.id}-t`} className="pillar-title">{p.title}</h3>
                <p className="muted">{p.lead}</p>
                <ul className="pillar-items">
                  {p.items.map((it) => (
                    <li key={it.key}>
                      <button
                        type="button"
                        id={it.key}
                        className="pillar-item"
                        onClick={() => (it.target ? navigate(it.target) : notify(it.title))}
                      >
                        <span className="pi-icon"><img src={it.icon} alt="" width={28} height={28} /></span>
                        <span className="pi-text">
                          <span className="pi-title">{it.title}</span>
                          <span className="pi-desc">{it.desc}</span>
                        </span>
                        <span className="pi-arrow" aria-hidden="true"><Icon name="arrow" /></span>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            </article>
          ))}
          <span className="pillar-seal" aria-hidden="true"><BrandMark size={56} /></span>
        </div>
      </div>
    </section>
  );
}

export function Courses({ courses, title = 'คอร์สสอนเทรด', sub = 'เรียนเป็นขั้นตอน จากพื้นฐานสู่ขั้นสูง เน้นวินัยและการบริหารความเสี่ยง', asPage = false }: { courses: Course[] | null; title?: string; sub?: string; asPage?: boolean }) {
  const anyDemo = !!courses?.some((c) => c.demo);
  return (
    <section className={`section ${asPage ? '' : 'section-lg'}`} id="courses" tabIndex={-1} aria-labelledby="courses-title">
      <div className="container">
        {!asPage && <SectionHead id="courses-title" eyebrow="TRADING ACADEMY" title={title} sub={sub} />}
        {asPage && <h2 id="courses-title" className="sr-only">{title}</h2>}
        {courses === null ? (
          <ul className="tracks">
            {COURSE_LEVELS.map((c) => (
              <li key={c.key} className="track track-trader">
                <span className="track-num" aria-hidden="true">{c.num}</span>
                <p className="track-kicker">คอร์สเทรด</p>
                <h3 className="track-title">ระดับ{c.level}</h3>
                <p className="muted">{c.desc}</p>
                <p className="track-status">รอประกาศรายละเอียดหลักสูตรและราคา</p>
              </li>
            ))}
          </ul>
        ) : courses.length === 0 ? (
          <p className="empty courses-empty"><span className="dot" aria-hidden="true" /> รอประกาศคอร์สเรียน ลงชื่อรับแจ้งได้ด้านล่าง</p>
        ) : (
          <ul className="tracks">
            {courses.map((c) => (
              <li key={c.id} className="track track-trader">
                <span className="track-num" aria-hidden="true">{LEVEL_NUM[c.level]}</span>
                <p className="track-kicker">คอร์สเทรด · ระดับ{LEVEL_LABEL[c.level]}</p>
                <h3 className="track-title">{c.title}</h3>
                <p className="muted">{c.desc}</p>
                <ul className="course-meta">
                  {c.lessons > 0 && <li><Icon name="book" /> {c.lessons} บทเรียน</li>}
                  {c.hours > 0 && <li><Icon name="calendar" /> {c.hours} ชั่วโมง</li>}
                </ul>
                <div className="course-foot">
                  <span className="course-price">{c.price > 0 ? thb(c.price) : 'รอประกาศราคา'}</span>
                  <span className={`course-status cs-${c.status}`}>{COURSE_STATUS_LABEL[c.status]}</span>
                </div>
                <Link to={`/courses/${c.id}`} className={`btn ${c.status === 'open' ? 'btn-primary' : 'btn-outline'}`} label={`ดูรายละเอียดคอร์ส ${c.title}`}>
                  ดูรายละเอียดคอร์ส <Icon name="arrow" />
                </Link>
                {c.demo && <p className="demo-note">ข้อมูลตัวอย่าง (Demo)</p>}
              </li>
            ))}
          </ul>
        )}
        {anyDemo && <p className="fine center">ชื่อคอร์ส รายละเอียด และราคาในส่วนนี้เป็นข้อมูลตัวอย่างสำหรับสาธิตระบบ</p>}
        <div className="center-cta">
          <button type="button" className="btn btn-primary btn-lg" onClick={() => goJoin('course')}>
            <Icon name="bell" /> ลงชื่อรับแจ้งเมื่อเปิดคอร์ส
          </button>
        </div>
      </div>
    </section>
  );
}

type HoroState = { kind: 'locked' } | { kind: 'loading' } | { kind: 'none' } | { kind: 'ok'; entry: HoroEntry };

export function Moves({ list, verb }: { list: HoroEntry['enters']; verb: string }) {
  if (!list.length) return <dd className="muted">ไม่มี</dd>;
  return <dd>{list.map((m, i) => <span key={i} className="move">ดาว{m.planet} {verb}ราศี{m.sign}</span>)}</dd>;
}

export function HoroChecker({ notify, auth }: { notify: Notify; auth: Auth }) {
  const [date, setDate] = useState(() => toISODate(new Date()));
  const [shown, setShown] = useState(date);
  const [state, setState] = useState<HoroState>({ kind: 'locked' });

  const load = useCallback(async (d: string, fromUser: boolean) => {
    setShown(d);
    setState({ kind: 'loading' });
    const r = await api<{ status: 'locked' | 'none' | 'ok'; entry?: HoroEntry }>('GET', `/api/public/horoscope?date=${encodeURIComponent(d)}`);
    if (!r.ok) {
      setState({ kind: 'locked' });
      if (fromUser) notify(r.offline ? 'ผลดวงการเทรด (เฉพาะสมาชิก)' : r.error);
      return;
    }
    const { status, entry } = r.data;
    setState(status === 'ok' && entry ? { kind: 'ok', entry } : status === 'none' ? { kind: 'none' } : { kind: 'locked' });
  }, [notify]);

  // Re-check whenever the member logs in or out, keeping the date the visitor picked.
  const memberId = auth.member?.id ?? '';
  const shownRef = useRef(shown);
  shownRef.current = shown;
  useEffect(() => { void load(shownRef.current, false); }, [load, memberId]);

  const onSubmit = (e: { preventDefault(): void }) => {
    e.preventDefault();
    if (date) void load(date, true);
  };

  return (
    <article className="card horo" id="horo" tabIndex={-1} aria-labelledby="horo-title">
      <div className="horo-body">
        <p className="kicker">
          {auth.member ? <><Icon name="user" /> สมาชิก: {auth.member.name}</> : <><Icon name="lock" /> เฉพาะสมาชิก</>}
        </p>
        <h2 id="horo-title" className="h2">เช็กดวงการเทรด</h2>
        <p className="muted">เลือกวัน เดือน ปี เพื่อดูว่าวันนั้นดาวใดเข้า ดาวใดย้ายออก และเหมาะแก่การเทรดหรือไม่</p>
        <form className="horo-form" onSubmit={onSubmit}>
          <label className="field">
            <span className="field-label">วันที่ต้องการเช็ก</span>
            <input id="horo-date" type="date" className="input" value={date} required onChange={(e: Event) => setDate((e.target as HTMLInputElement).value)} />
          </label>
          <button type="submit" className="btn btn-primary">เช็กดวง <Icon name="arrow" /></button>
        </form>
      </div>

      <div className={`horo-result is-${state.kind}`} aria-live="polite" aria-busy={state.kind === 'loading'}>
        <p className="horo-date">
          <Icon name="calendar" /> {shown ? thaiDate(shown) : '—'}
          {state.kind === 'ok' && state.entry.demo && <span className="demo-badge">ข้อมูลตัวอย่าง ไม่ใช่ดวงจริง</span>}
        </p>
        {state.kind === 'ok' ? (
          <>
            <dl className="horo-rows is-open">
              <div><dt>ดาวเข้า</dt><Moves list={state.entry.enters} verb="เข้า" /></div>
              <div><dt>ดาวย้ายออก</dt><Moves list={state.entry.leaves} verb="ย้ายออกจาก" /></div>
            </dl>
            <p className={`verdict-big v-${state.entry.verdict}`}>
              <Icon name={state.entry.verdict === 'good' ? 'check' : state.entry.verdict === 'bad' ? 'close' : 'info'} />
              {VERDICT_LABEL[state.entry.verdict]}
            </p>
            {state.entry.note && <p className="horo-note">{state.entry.note}</p>}
          </>
        ) : state.kind === 'none' ? (
          <div className="empty"><span className="dot" aria-hidden="true" /> ยังไม่มีข้อมูลดวงการเทรดสำหรับวันนี้</div>
        ) : (
          <>
            <dl className="horo-rows">
              <div><dt>ดาวเข้า</dt><dd>ดาว ••••• เข้าราศี •••••</dd></div>
              <div><dt>ดาวย้ายออก</dt><dd>ดาว ••••• ย้ายออกจากราศี •••••</dd></div>
            </dl>
            <div className="verdicts" aria-hidden="true">
              <span className="verdict good"><Icon name="check" /> เหมาะแก่การเทรด</span>
              <span className="verdict bad"><Icon name="close" /> ไม่เหมาะแก่การเทรด</span>
            </div>
            {state.kind === 'locked' && (
              <div className="horo-lock">
                <span className="lock-orb"><Icon name="lock" /></span>
                <p className="h3">ผลดวงการเทรดเปิดให้สมาชิกเท่านั้น</p>
                <div className="lock-actions">
                  <button type="button" className="btn btn-primary" onClick={() => goJoin('horo')}>สมัครสมาชิก</button>
                  <button type="button" className="btn btn-ghost" onClick={() => (auth.online ? auth.openLogin() : notify('เข้าสู่ระบบ'))}>เข้าสู่ระบบ</button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </article>
  );
}

export function HoroAndLive({ notify, auth }: { notify: Notify; auth: Auth }) {
  return (
    <section className="section" aria-label="เช็กดวงการเทรดและไลฟ์">
      <div className="container split">
        <HoroChecker notify={notify} auth={auth} />

        <article className="card live" id="live" aria-labelledby="live-title">
          <Candles className="live-candles" count={16} trend={0.6} />
          {OWNER_PORTRAIT_SM && <img className="live-portrait" src={OWNER_PORTRAIT_SM} alt="" width={OWNER_PORTRAIT_SM_SIZE.w} height={OWNER_PORTRAIT_SM_SIZE.h} />}
          <div className={`live-body ${OWNER_PORTRAIT_SM ? 'has-portrait' : ''}`}>
            <div className="live-head">
              <span className="live-pill is-off"><Icon name="live" /> LIVE</span>
              <h2 id="live-title" className="h2 italic">ถ่ายทอดสด</h2>
            </div>
            <p className="muted">ไลฟ์สอนเทรดและอัปเดตจังหวะดวงดาว สำหรับสมาชิก</p>
            <div className="empty">
              <span className="dot" aria-hidden="true" />
              <span>ยังไม่มีการถ่ายทอดสดในขณะนี้</span>
            </div>
            <button type="button" className="btn btn-outline" onClick={() => notify('แจ้งเตือนเมื่อเริ่มไลฟ์')}>
              <Icon name="bell" /> แจ้งเตือนเมื่อเริ่มไลฟ์
            </button>
          </div>
        </article>
      </div>
    </section>
  );
}

export function Journey({ notify }: { notify: Notify }) {
  return (
    <section className="section section-lg" id="member" aria-labelledby="journey-title">
      <div className="container">
        <SectionHead id="journey-title" eyebrow="MEMBERSHIP" title="เส้นทางสมาชิก" sub="พื้นที่ส่วนตัวที่เติบโตไปพร้อมกับคุณ ทุกวัน ทุกบทเรียน" />
        <ol className="journey">
          {JOURNEY.map((j, i) => (
            <li key={j.key} className="step">
              <span className="step-orb"><Icon name={j.icon} /><span className="step-n">{i + 1}</span></span>
              <span className="step-title">{j.title}</span>
              <span className="step-desc">{j.desc}</span>
            </li>
          ))}
        </ol>
        <div className="referral">
          <span className="ref-icon"><Icon name="users" /></span>
          <div className="ref-copy">
            <p className="h3">ชวนเพื่อนมาเรียนด้วยกัน</p>
            <p className="muted small">แบ่งปันเส้นทางโหราเทรดเดอร์ให้คนที่สนใจการเทรดอย่างมีวินัย</p>
          </div>
          <button type="button" className="btn btn-outline" onClick={() => notify('ชวนเพื่อน')}>ชวนเพื่อน</button>
        </div>
      </div>
    </section>
  );
}

/** Home-page preview of the latest daily news. */
export function LatestNews({ news }: { news: NewsItem[] | null }) {
  if (!news || news.length === 0) return null;
  return (
    <section className="section" aria-labelledby="news-title">
      <div className="container">
        <div className="section-head">
          <h2 id="news-title" className="h2">ข่าวสารล่าสุด</h2>
          <Link to="/news" className="link-btn">ดูทั้งหมด <Icon name="arrow" /></Link>
        </div>
        <ul className="card-grid">
          {news.slice(0, 3).map((n) => <li key={n.id}><NewsCard item={n} /></li>)}
        </ul>
      </div>
    </section>
  );
}

export function Clips({ clips, onLocked }: { clips: Clip[] | null; onLocked: () => void }) {
  const has = !!clips && clips.length > 0;
  return (
    <section className="section" id="clips" tabIndex={-1} aria-labelledby="clips-title">
      <div className="container">
        <div className="section-head">
          <h2 id="clips-title" className="h2">คลังวิดีโอ</h2>
          <Link to="/clips" className="link-btn">ดูทั้งหมด <Icon name="arrow" /></Link>
        </div>
        {has ? (
          <ul className="card-grid">
            {clips!.slice(0, 3).map((c) => <li key={c.id}><ClipCard clip={c} onLocked={onLocked} /></li>)}
          </ul>
        ) : (
        <div className="card clips-empty">
          <div className="clips-art" aria-hidden="true">
            <img src={artTrade} alt="" width={640} height={360} />
            <img src={artHoro} alt="" width={640} height={360} />
            <img src={artLive} alt="" width={640} height={360} />
            <span className="play"><Icon name="play" /></span>
          </div>
          <div className="clips-copy">
            <p className="h3">ยังไม่มีคลิปที่เผยแพร่</p>
            <p className="muted">คลิปสอนเทรดและอัปเดตดวงการเทรดจะแสดงที่นี่เมื่อเชื่อมต่อคลังวิดีโอแล้ว</p>
            <p className="fine">ภาพประกอบเท่านั้น ไม่ใช่คลิปจริง</p>
          </div>
        </div>
        )}
      </div>
    </section>
  );
}

export function Articles({ articles }: { articles: Article[] | null }) {
  return (
    <section className="section" id="articles" tabIndex={-1} aria-labelledby="articles-title">
      <div className="container">
        <div className="section-head">
          <h2 id="articles-title" className="h2">บทความ</h2>
          <Link to="/articles" className="link-btn">ดูทั้งหมด <Icon name="arrow" /></Link>
        </div>
        {articles && articles.length > 0 ? (
          <ul className="card-grid">
            {articles.slice(0, 3).map((a) => <li key={a.id}><ArticleCard item={a} /></li>)}
          </ul>
        ) : (
        <ul className="news-grid">
          {ARTICLE_TOPICS.map((t) => (
            <li key={t.key} className="news-card">
              <img src={t.img} alt="" width={176} height={112} />
              <div className="news-body">
                <h3 className="h3">{t.title}</h3>
                <p className="muted small">{t.desc}</p>
                <p className="fine">ยังไม่มีบทความในหมวดนี้</p>
              </div>
            </li>
          ))}
        </ul>
        )}
      </div>
    </section>
  );
}

export function PrivacyDialog({ privacy, onClose }: { privacy: Privacy | null; onClose: () => void }) {
  const ref = useModalDialog(onClose);
  return (
    <dialog ref={ref} className="policy-dialog" aria-labelledby="policy-title">
      <div className="policy-head">
        <h2 id="policy-title" className="h2">{privacy?.title ?? 'นโยบายความเป็นส่วนตัว'}</h2>
        <button type="button" className="btn btn-icon" aria-label="ปิด" onClick={onClose}><Icon name="close" /></button>
      </div>
      {privacy?.body.trim() ? <PolicyBody body={privacy.body} /> : <p className="muted">ยังไม่ได้เผยแพร่นโยบายความเป็นส่วนตัว</p>}
    </dialog>
  );
}

/** Members change their own password (e.g. after the team issues a temporary one). */
function ChangePassword() {
  const [open, setOpen] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const submit = async (e: { preventDefault(): void; currentTarget: HTMLFormElement }) => {
    e.preventDefault();
    const form = e.currentTarget;
    const d = new FormData(form);
    const current = String(d.get('current') ?? ''), pw = String(d.get('new') ?? ''), pw2 = String(d.get('new2') ?? '');
    if (pw.length < 8) return setMsg({ ok: false, text: 'รหัสผ่านใหม่ต้องมีอย่างน้อย 8 ตัวอักษร' });
    if (pw !== pw2) return setMsg({ ok: false, text: 'ยืนยันรหัสผ่านใหม่ไม่ตรงกัน' });
    setBusy(true);
    const r = await api('POST', '/api/member/password', { current, password: pw });
    setBusy(false);
    if (r.ok) { form.reset(); setMsg({ ok: true, text: 'เปลี่ยนรหัสผ่านแล้ว' }); } else setMsg({ ok: false, text: r.error });
  };
  if (!open) return <button type="button" className="link-plain" onClick={() => setOpen(true)}>เปลี่ยนรหัสผ่าน</button>;
  return (
    <form className="pw-form" onSubmit={submit} noValidate>
      <p className="field-label">เปลี่ยนรหัสผ่าน</p>
      <label className="field"><span className="field-label">รหัสผ่านปัจจุบัน</span><input name="current" type="password" className="input" autoComplete="current-password" /></label>
      <label className="field"><span className="field-label">รหัสผ่านใหม่</span><input name="new" type="password" className="input" autoComplete="new-password" /></label>
      <label className="field"><span className="field-label">ยืนยันรหัสผ่านใหม่</span><input name="new2" type="password" className="input" autoComplete="new-password" /></label>
      {msg && <p className={msg.ok ? 'form-success' : 'form-errors'} role={msg.ok ? 'status' : 'alert'}>{msg.text}</p>}
      <div className="lock-actions">
        <button type="submit" className="btn btn-primary" disabled={busy}>บันทึกรหัสผ่าน</button>
        <button type="button" className="btn btn-ghost" onClick={() => { setOpen(false); setMsg(null); }}>ยกเลิก</button>
      </div>
    </form>
  );
}

/** Members' area shown in place of the sign-up form once logged in. */
export function MemberWelcome({ member, enrollments, onLogout }: { member: PublicMember; enrollments: Enrollment[]; onLogout: () => void }) {
  return (
    <div className="join-form member-card" role="region" aria-label="พื้นที่สมาชิก">
      <p className="kicker"><Icon name="user" /> พื้นที่สมาชิก</p>
      <p className="h3">สวัสดีคุณ{member.name}</p>
      <p className="muted small">สมาชิกตั้งแต่ {thaiDate(member.createdAt.slice(0, 10), false)}</p>
      <div className="my-courses">
        <p className="field-label">คอร์สของฉัน</p>
        {enrollments.length === 0 ? <p className="muted small">ยังไม่มีคอร์ส เลือกคอร์สที่สนใจได้ที่หน้าคอร์สเทรด</p> : (
          <ul>
            {enrollments.map((e) => (
              <li key={e.courseId}>
                {e.published ? <Link to={`/courses/${e.courseId}`}>{e.title ?? 'คอร์ส'}</Link> : <span className="course-hidden">{e.title ?? 'คอร์ส'}</span>}
                <span className={`enroll-badge en-${e.status}`}>{ENROLL_LABEL[e.status]}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
      <ul className="member-links">
        <li><Link to="/horoscope" className="btn btn-primary" focus="horo-date"><Icon name="moon" /> เช็กดวงการเทรด</Link></li>
        <li><Link to="/courses" className="btn btn-outline"><Icon name="book" /> ดูคอร์สสอนเทรด</Link></li>
      </ul>
      <dl className="member-info">
        {member.email && <div><dt>อีเมล</dt><dd>{member.email}</dd></div>}
        {member.phone && <div><dt>เบอร์โทร</dt><dd>{member.phone}</dd></div>}
        {member.line && <div><dt>LINE ID</dt><dd>{member.line}</dd></div>}
      </dl>
      <ChangePassword />
      <button type="button" className="link-plain" onClick={onLogout}>ออกจากระบบ</button>
    </div>
  );
}

/** Sign-up = member account + CRM lead (server links them). Offline builds show the "not active" toast. */
export function Join({ notify, privacy, auth, courses = [] }: { notify: Notify; privacy: Privacy | null; auth: Auth; courses?: Course[] }) {
  const [course, setCourse] = useState(() => pendingCourse);
  useEffect(() => { pendingCourse = ''; }, []);
  const [errors, setErrors] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [showPolicy, setShowPolicy] = useState(false);
  const errRef = useRef<HTMLDivElement | null>(null);
  const closePolicy = useCallback(() => setShowPolicy(false), []);

  // Pre-tick the interest chosen on another page (e.g. "ลงชื่อรอเปิด" on a course page).
  useEffect(() => {
    if (!pendingInterest) return;
    const box = document.getElementById(`int-${pendingInterest}`) as HTMLInputElement | null;
    if (box) box.checked = true;
    pendingInterest = null;
  }, []);

  const fail = (errs: string[]) => {
    setErrors(errs);
    window.setTimeout(() => errRef.current?.focus(), 0);
  };

  const onSubmit = async (e: { preventDefault(): void; currentTarget: HTMLFormElement }) => {
    e.preventDefault();
    const form = e.currentTarget;
    const d = new FormData(form);
    const val = (k: string) => String(d.get(k) ?? '').trim();
    const errs: string[] = [];
    if (!val('name')) errs.push('กรุณากรอกชื่อ');
    if (!val('phone') && !val('email')) errs.push('กรุณากรอกอีเมลหรือเบอร์โทร เพื่อใช้เข้าสู่ระบบ');
    if (val('phone') && val('phone').replace(/\D/g, '').length < 9) errs.push('เบอร์โทรไม่ถูกต้อง');
    if (val('email') && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val('email'))) errs.push('รูปแบบอีเมลไม่ถูกต้อง');
    const pw = String(d.get('password') ?? '');
    if (pw.length < 8) errs.push('รหัสผ่านต้องมีอย่างน้อย 8 ตัวอักษร');
    else if (pw !== String(d.get('password2') ?? '')) errs.push('ยืนยันรหัสผ่านไม่ตรงกัน');
    if (!d.get('consent')) errs.push('กรุณายินยอมให้เก็บและใช้ข้อมูลก่อนส่ง');
    if (errs.length) return fail(errs);
    setErrors([]);
    setBusy(true);
    const r = await api<{ member: PublicMember | null }>('POST', '/api/member/register', {
      name: val('name'), phone: val('phone'), line: val('line'), email: val('email'), password: pw,
      interests: d.getAll('interest').map(String), consent: true, website: val('website'), courseId: val('courseId'),
    });
    setBusy(false);
    if (r.ok) {
      form.reset();
      if (r.data.member) auth.signedIn(r.data.member);
      return;
    }
    if (r.offline) notify('สมัครสมาชิก');
    else fail([r.error]);
  };

  return (
    <section className="section" id="join" tabIndex={-1} aria-labelledby="join-title">
      <div className="container">
        <div className="join">
          <img className="join-strip" src={artCosmos} alt="" width={640} height={400} />
          <div className="join-intro">
            <Crown size={56} />
            <h2 id="join-title" className="h2 italic">เริ่มต้นเส้นทางโหราเทรดเดอร์</h2>
            <p className="muted">{auth.member ? 'ขอบคุณที่เป็นส่วนหนึ่งของโหราเทรดเดอร์' : 'สมัครสมาชิกเพื่อรับสิทธิ์และข่าวสารจากเรา'}</p>
            <ul className="join-benefits">
              <li><Icon name="moon" /> เช็กดวงการเทรดตามวัน เดือน ปี</li>
              <li><Icon name="book" /> รับแจ้งก่อนใครเมื่อเปิดคอร์สสอนเทรด</li>
              <li><Icon name="chart" /> ติดตามพัฒนาการการเทรดของคุณ</li>
            </ul>
          </div>

          {auth.member ? <MemberWelcome member={auth.member} enrollments={auth.enrollments} onLogout={auth.logout} /> : (
          <form className="join-form" noValidate onSubmit={onSubmit} aria-describedby={errors.length ? 'join-errors' : undefined}>
            {errors.length > 0 && (
              <div id="join-errors" ref={errRef} className="form-errors" role="alert" tabIndex={-1}>
                <p>กรุณาตรวจสอบข้อมูล</p>
                <ul>{errors.map((m) => <li key={m}>{m}</li>)}</ul>
              </div>
            )}
            <p className="form-title full">สมัครสมาชิก</p>
            <label className="hp" aria-hidden="true">
              เว็บไซต์ <input name="website" tabIndex={-1} autoComplete="off" />
            </label>
            <label className="field full">
              <span className="field-label">ชื่อ <span className="req" aria-hidden="true">*</span></span>
              <input id="join-name" name="name" className="input" autoComplete="name" required />
            </label>
            <label className="field">
              <span className="field-label">เบอร์โทร</span>
              <input name="phone" type="tel" className="input" autoComplete="tel" inputMode="tel" />
            </label>
            <label className="field">
              <span className="field-label">อีเมล</span>
              <input name="email" type="email" className="input" autoComplete="email" />
            </label>
            <p className="fine full field-hint">ใช้อีเมลหรือเบอร์โทรเพื่อเข้าสู่ระบบ กรอกอย่างน้อย 1 ช่อง</p>
            <label className="field full">
              <span className="field-label">LINE ID (ไม่บังคับ)</span>
              <input name="line" className="input" autoComplete="off" />
            </label>
            <label className="field">
              <span className="field-label">รหัสผ่าน <span className="req" aria-hidden="true">*</span></span>
              <input name="password" type="password" className="input" autoComplete="new-password" minLength={8} required />
            </label>
            <label className="field">
              <span className="field-label">ยืนยันรหัสผ่าน <span className="req" aria-hidden="true">*</span></span>
              <input name="password2" type="password" className="input" autoComplete="new-password" required />
            </label>
            {courses.length > 0 && (
              <label className="field full">
                <span className="field-label">คอร์สที่สนใจ (ไม่บังคับ)</span>
                <select id="join-course" name="courseId" className="input" value={course} onChange={(e: Event) => setCourse((e.target as HTMLSelectElement).value)}>
                  <option value="">ยังไม่เลือกคอร์ส</option>
                  {courses.filter((c) => c.status !== 'closed').map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
                </select>
              </label>
            )}
            <fieldset className="field full interests">
              <legend className="field-label">สนใจเรื่อง</legend>
              <div className="chips">
                {INTERESTS.map((it) => (
                  <label key={it.key} className="chip">
                    <input id={`int-${it.key}`} type="checkbox" name="interest" value={it.key} />
                    <span>{it.label}</span>
                  </label>
                ))}
              </div>
            </fieldset>
            <label className="consent full">
              <input type="checkbox" name="consent" />
              <span>
                ยินยอมให้โหราเทรดเดอร์เก็บและใช้ข้อมูลนี้เพื่อติดต่อเรื่องสมาชิกและคอร์สเรียน
                ไม่ส่งต่อให้บุคคลภายนอก และขอยกเลิกได้ทุกเมื่อ
              </span>
            </label>
            <div className="form-actions full">
              <button type="submit" className="btn btn-primary btn-lg" disabled={busy}>{busy ? 'กำลังสมัคร…' : 'สมัครสมาชิก'} <Icon name="arrow" /></button>
              <button type="button" className="link-plain" onClick={() => setShowPolicy(true)}>อ่านนโยบายความเป็นส่วนตัว</button>
            </div>
            <p className="auth-switch full">
              เป็นสมาชิกอยู่แล้ว?{' '}
              <button type="button" className="link-btn" onClick={() => (auth.online ? auth.openLogin() : notify('เข้าสู่ระบบ'))}>เข้าสู่ระบบ</button>
            </p>
          </form>
          )}
        </div>
      </div>
      {showPolicy && <PrivacyDialog privacy={privacy} onClose={closePolicy} />}
    </section>
  );
}

export function Footer({ notify, auth, lineAddUrl }: { notify: Notify; auth: Auth; lineAddUrl: string }) {
  return (
    <footer className="site-footer">
      <div className="planets" aria-hidden="true">
        <img src={moon} alt="" width={80} height={72} />
        <img src={earth} alt="" width={85} height={74} />
        <img src={saturn} alt="" width={117} height={72} />
      </div>
      <div className="container footer-grid">
        <div>
          <div className="brand static">
            <BrandLockup size={64} />
          </div>
          <p className="muted small">คอร์สสอนเทรด และเครื่องมือเช็กดวงการเทรดสำหรับสมาชิก</p>
        </div>
        <nav aria-label="ลิงก์ส่วนท้าย">
          <ul className="footer-links">
            {NAV.map((n) => (
              <li key={n.to}><Link to={n.to}>{n.label}</Link></li>
            ))}
            <li><Link to="/privacy">นโยบายความเป็นส่วนตัว</Link></li>
            {lineAddUrl && <li><a href={lineAddUrl} target="_blank" rel="noopener noreferrer">LINE OA โหราเทรดเดอร์</a></li>}
            {auth.member ? (
              <li><button type="button" className="link-plain" onClick={auth.logout}>ออกจากระบบ</button></li>
            ) : (
              <>
                <li><button type="button" className="link-plain" onClick={() => (auth.online ? auth.openLogin() : notify('เข้าสู่ระบบ'))}>เข้าสู่ระบบ</button></li>
                <li><button type="button" className="link-plain" onClick={() => goJoin()}>สมัครสมาชิก</button></li>
              </>
            )}
          </ul>
        </nav>
      </div>
      <div className="container footer-bottom">
        <span>© {new Date().getFullYear() + 543} โหราเทรดเดอร์</span>
        <span>ต้นแบบหน้าเว็บ — ภาพประกอบเป็นภาพตัวอย่าง</span>
      </div>
    </footer>
  );
}

